// Voice preview utilities for the Agent Wizard

export interface VoicePreviewOptions {
  text?: string;
  language?: string;
  apiKey: string;
}

export class VoicePreviewManager {
  private currentAudio: HTMLAudioElement | null = null;
  private previewCache: Map<string, string> = new Map();

  /**
   * Play a preview of a voice
   * @param voiceId The ID of the voice to preview
   * @param options Preview options including API key and custom text
   */
  async playPreview(voiceId: string, options: VoicePreviewOptions): Promise<void> {
    // Stop any currently playing preview
    this.stopPreview();

    try {
      // Check cache first
      const cacheKey = `${voiceId}_${options.text || 'default'}_${options.language || 'en'}`;
      let audioUrl = this.previewCache.get(cacheKey);

      if (!audioUrl) {
        // Generate preview audio
        audioUrl = await this.generatePreview(voiceId, options);
        this.previewCache.set(cacheKey, audioUrl);
      }

      // Play the audio
      this.currentAudio = new Audio(audioUrl);
      this.currentAudio.volume = 0.8;
      
      await this.currentAudio.play();
      
      // Clean up when finished
      this.currentAudio.addEventListener('ended', () => {
        this.currentAudio = null;
      });
    } catch (error) {
      console.error('Failed to play voice preview:', error);
      throw new Error('Unable to play voice preview. Please try again.');
    }
  }

  /**
   * Stop the currently playing preview
   */
  stopPreview(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  }

  /**
   * Check if a preview is currently playing
   */
  isPlaying(): boolean {
    return this.currentAudio !== null && !this.currentAudio.paused;
  }

  /**
   * Generate preview audio from ElevenLabs API
   */
  private async generatePreview(
    voiceId: string, 
    options: VoicePreviewOptions
  ): Promise<string> {
    const defaultText = this.getDefaultPreviewText(options.language || 'en');
    const text = options.text || defaultText;

    const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
      method: 'POST',
      headers: {
        'xi-api-key': options.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        model_id: 'eleven_turbo_v2_5', // Use fast model for previews
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.75,
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate voice preview');
    }

    const audioBlob = await response.blob();
    return URL.createObjectURL(audioBlob);
  }

  /**
   * Get default preview text for different languages
   */
  private getDefaultPreviewText(language: string): string {
    const previewTexts: Record<string, string> = {
      en: "Hello! I'm your AI assistant. How can I help you today?",
      es: "¡Hola! Soy tu asistente de IA. ¿Cómo puedo ayudarte hoy?",
      fr: "Bonjour! Je suis votre assistant IA. Comment puis-je vous aider aujourd'hui?",
      de: "Hallo! Ich bin Ihr KI-Assistent. Wie kann ich Ihnen heute helfen?",
      it: "Ciao! Sono il tuo assistente AI. Come posso aiutarti oggi?",
      pt: "Olá! Eu sou seu assistente de IA. Como posso ajudá-lo hoje?",
      ja: "こんにちは！私はあなたのAIアシスタントです。今日はどのようにお手伝いできますか？",
      zh: "你好！我是你的AI助手。今天我能为你做些什么？",
      ko: "안녕하세요! 저는 당신의 AI 어시스턴트입니다. 오늘 어떻게 도와드릴까요?",
    };

    return previewTexts[language] || previewTexts.en;
  }

  /**
   * Clear the preview cache
   */
  clearCache(): void {
    // Revoke all cached URLs to free memory
    this.previewCache.forEach(url => URL.revokeObjectURL(url));
    this.previewCache.clear();
  }
}

// Export a singleton instance
export const voicePreviewManager = new VoicePreviewManager();

// Hook for React components
export function useVoicePreview(apiKey: string) {
  const playPreview = async (voiceId: string, language?: string, text?: string) => {
    await voicePreviewManager.playPreview(voiceId, { apiKey, language, text });
  };

  const stopPreview = () => {
    voicePreviewManager.stopPreview();
  };

  const isPlaying = () => {
    return voicePreviewManager.isPlaying();
  };

  return { playPreview, stopPreview, isPlaying };
}